import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Upload, FileText, Loader2, Send, Clock } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";

const STATUS_COLORS = {
  planning: "bg-blue-100 text-blue-700", scheduled: "bg-purple-100 text-purple-700",
  in_progress: "bg-primary/10 text-primary", on_hold: "bg-amber-100 text-amber-700", completed: "bg-green-100 text-green-700",
};
const label = (s) => s.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem("cg_token");

  const loadProject = () => api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => {});
  const loadMessages = () => api.get(`/projects/${id}/messages`).then((r) => setMessages(r.data)).catch(() => {});
  useEffect(() => { loadProject(); loadMessages(); }, [id]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("scope", "project");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const f2 = new FormData();
      f2.append("file_id", data.id); f2.append("name", file.name);
      const { data: updated } = await api.post(`/projects/${id}/files`, f2, { headers: { "Content-Type": "multipart/form-data" } });
      setProject(updated);
      toast.success("File uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const sendMessage = async () => {
    if (!msg.trim()) return;
    try { const { data } = await api.post(`/projects/${id}/messages`, { text: msg }); setMessages((m) => [...m, data]); setMsg(""); }
    catch { toast.error("Message failed"); }
  };

  if (!project) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <Link to="/portal" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"><ArrowLeft className="h-4 w-4" /> Back to projects</Link>

      <div className="bg-card border border-border p-6">
        <div className="flex items-start justify-between gap-3">
          <div><h1 className="font-heading text-2xl font-extrabold">{project.title}</h1><p className="text-muted-foreground">{project.service_type}{project.address ? ` · ${project.address}` : ""}</p></div>
          <span className={`text-xs font-bold px-3 py-1.5 shrink-0 ${STATUS_COLORS[project.status]}`}>{label(project.status)}</span>
        </div>
        <div className="mt-5">
          <div className="flex justify-between text-sm mb-1"><span className="text-muted-foreground">Overall progress</span><span className="font-bold">{project.progress}%</span></div>
          <Progress value={project.progress} className="h-3" />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Updates timeline */}
        <div className="bg-card border border-border p-6">
          <h2 className="font-heading text-lg font-bold mb-4">Project Updates</h2>
          {(project.updates?.length || 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No updates yet. We'll post progress here.</p>
          ) : (
            <div className="space-y-4">
              {project.updates.slice().reverse().map((u, i) => (
                <div key={i} className="flex gap-3" data-testid={`update-${i}`}>
                  <div className="flex flex-col items-center"><div className="h-2.5 w-2.5 rounded-full bg-primary mt-1.5" />{i < project.updates.length - 1 && <div className="w-px flex-1 bg-border" />}</div>
                  <div className="pb-2"><p className="text-sm">{u.text}</p><p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(u.date).toLocaleDateString()}</p></div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Files */}
        <div className="bg-card border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold">Files & Photos</h2>
            <Button onClick={() => fileRef.current?.click()} size="sm" variant="outline" className="rounded-sm font-bold" data-testid="portal-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" /> Upload</>}
            </Button>
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => uploadFile(e.target.files[0])} data-testid="portal-file-input" />
          </div>
          {(project.files?.length || 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No files shared yet. Upload site photos or documents here.</p>
          ) : (
            <div className="space-y-2">
              {project.files.map((f, i) => (
                <a key={i} href={`${fileUrl(f.id)}?auth=${token}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 bg-muted hover:bg-muted/70 transition-colors" data-testid={`portal-file-${i}`}>
                  <FileText className="h-4 w-4 text-primary" /><span className="text-sm font-medium truncate flex-1">{f.name}</span><span className="text-xs text-muted-foreground">{f.by}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="bg-card border border-border p-6">
        <h2 className="font-heading text-lg font-bold mb-4">Messages</h2>
        <div className="space-y-3 max-h-72 overflow-y-auto mb-4">
          {messages.length === 0 ? <p className="text-sm text-muted-foreground">No messages yet. Send a note to our team.</p> :
            messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender_id === user.user_id ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                <div className={`max-w-[75%] p-3 text-sm ${m.sender_id === user.user_id ? "bg-primary text-white" : "bg-muted"}`}>
                  <p>{m.text}</p>
                  <p className={`text-[10px] mt-1 ${m.sender_id === user.user_id ? "text-white/70" : "text-muted-foreground"}`}>{m.sender_name} · {new Date(m.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
        </div>
        <div className="flex gap-2">
          <Input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMessage()} placeholder="Type a message…" className="rounded-sm" data-testid="message-input" />
          <Button onClick={sendMessage} className="bg-primary rounded-sm font-bold shrink-0" data-testid="message-send"><Send className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
